interface DotProps {
  isActive: boolean;
  isCompleted: boolean;
  isFirst: boolean;
  isLast: boolean;
}

const Dot = ({ isActive, isCompleted, isFirst, isLast }: DotProps) => {
  const activeClass = isActive ? 'bg-white border-green-dark' : 'bg-gray';
  const completedClass = isCompleted ? 'bg-green-dark border-green-dark' : '';

  return (
    <div
      aria-hidden={true}
      className={`w-full relative flex items-center ${
        isFirst ? 'justify-start' : isLast ? 'justify-end' : 'justify-center'
      }`}
    >
      <div
        className={`relative icon-24 border-4 rounded-full border-gray transition-colors ${activeClass} ${completedClass}`}
      >
        {isCompleted && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 76 76"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
            className="text-white inline-block absolute top-0 left-0 mt-px"
          >
            <path d="M74.998 3.518a4.264 4.264 0 0 0-5.81-.672c-.86.644-20.772 15.698-45.932 49.953L6.836 40.367a4.261 4.261 0 0 0-5.666.465 4.26 4.26 0 0 0-.164 5.681l21.878 25.92a4.26 4.26 0 0 0 7.242-1.244c.116-.307 11.895-30.886 44.543-61.835A4.26 4.26 0 0 0 75 3.518" />
          </svg>
        )}
      </div>
    </div>
  );
};

interface StepperItemProps {
  activeIndex: number;
  name: string;
  selfIndex: number;
  length: number;
  percentComplete: number;
}

const StepperItem = ({
  activeIndex,
  selfIndex,
  length,
  name,
  percentComplete,
}: StepperItemProps) => {
  const isActive = selfIndex === activeIndex;
  const isFirst = selfIndex === 0;
  const isCompleted = selfIndex < activeIndex;
  const isLast = selfIndex === length - 1;

  let progress = '0';
  let Label = null;
  let ProgressBar = null;

  if (!isActive) {
    // Inactive Step
    // If more than 2 items, hide text for inactive step on small screens
    Label = (
      <div
        // dot height of 24 + 8 gutter = top-32
        className={`absolute top-32 whitespace-pre ${
          length > 2 ? 'transition-opacity opacity-0 md:opacity-100' : ''
        }`}
      >
        {name}
      </div>
    );
  }

  if (isActive) {
    progress = `${percentComplete}%`;
    Label = (
      <div
        className={`font-bold absolute top-32 whitespace-pre transition-opacity opacity-100 ${
          isLast && name?.length > 11 ? '-right-24 md:right-auto' : ''
        }`}
      >
        {name}
      </div>
    );
  }

  if (isCompleted) {
    progress = '100%';
  }

  if (!isLast) {
    ProgressBar = (
      <li className="flex-grow h-4 bg-gray" aria-hidden={true}>
        <div
          className="flex h-4 bg-green-dark will-change-transform transition-all"
          style={{
            width: progress,
          }}
        />
      </li>
    );
  }

  return (
    <>
      {/* Item */}
      <li
        className="text-center flex flex-col items-center justify-center relative"
        aria-current={isActive ? 'step' : false}
      >
        <Dot {...{ isActive, isFirst, isCompleted, isLast }} />
        {Label}
      </li>
      {ProgressBar}
    </>
  );
};

interface ScreenReaderProps {
  current: number;
  total: number;
  name: string;
}

// TODO should the screen reader live region be moved to the main index component entry and announce broader contextual info?
const ScreenReader = ({ current, total, name }: ScreenReaderProps) => {
  return (
    // Step 1 of 3, Move Out
    <span className="sr-only" role="alert" aria-live="polite" aria-atomic="true">
      {`Step ${current} of ${total}, ${name}`}
    </span>
  );
};

interface StepperProps {
  activeIndex: number;
  steps: string[];
  srStepLabels?: string[];
  percentComplete: number;
}

const Stepper = ({ activeIndex, steps, srStepLabels, percentComplete }: StepperProps) => (
  <div className="px-24 my-24" id="mam:stepper">
    <ScreenReader
      current={activeIndex + 1}
      total={steps.length}
      name={srStepLabels?.[activeIndex] || steps[activeIndex]}
    />
    {/* Hide the list from the screen-reader with aria-hidden, instead we announce the screenReaderText in an updating region */}
    <ol className="flex justify-between items-center w-full">
      {steps.map((name, index) => {
        return (
          <StepperItem
            key={index}
            selfIndex={index}
            length={steps.length}
            {...{ activeIndex, name, percentComplete }}
          />
        );
      })}
    </ol>
  </div>
);

export { Stepper };
